import { Box } from "@mui/material";
import { MRT_Row, type MRT_ColumnDef } from "material-react-table";
import { useMemo } from "react";

import { RiskBaseModel } from "@/features/risk/types/riskModels";
import { OverviewItemsModel } from "@/shared/components/baselineOverview";
import Footer from "@/shared/components/FooterForColumns";
import { RNumberInputLabel, RNumberLabel } from "@/shared/components/inputs";
import RTextInputLabel from "@/shared/components/inputs/textInputs/RTextInputLabel";
import { LONG_NAMES, SMALL_AMOUNT_RANGE } from "@/shared/constants";

type HandleSaveCellType = (
  riskToUpdate: RiskBaseModel,
  name: string,
  value: number | string
) => void;

const ColumnsForRiskTable = ({
  overviewItems,
  handleSaveCell,
}: {
  overviewItems: OverviewItemsModel[];
  handleSaveCell: HandleSaveCellType;
}) =>
  useMemo<MRT_ColumnDef<RiskBaseModel>[]>(
    () => [
      {
        accessorKey: "name",
        header: "Name",
        minSize: 90,
        size: 150,
        maxSize: 250,
        muiTableBodyCellProps: {
          align: "left",
        },
        Cell: ({ row }: { row: MRT_Row<RiskBaseModel> }) => (
          <RTextInputLabel
            value={row.original.name}
            onInputChange={(newValue) => {
              void handleSaveCell(row.original, "name", newValue);
            }}
            maxLength={LONG_NAMES} // Use your constant for max length
            typographyProps={{ fontWeight: "bold" }}
            multiline={true}
          />
        ),
      },
      {
        accessorKey: "probabOfOccurrence",
        header: "Probability",
        size: 55,
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ row }: { row: MRT_Row<RiskBaseModel> }) => {
          return (
            <RNumberInputLabel
              value={row.original.probabOfOccurrence ?? 0}
              category={"percentage"}
              onInputChange={(newValue) => {
                void handleSaveCell(row.original, "probabOfOccurrence", newValue);
              }}
              allowNegative
              decimalScaleInput={6}
              decimalScaleLabel={2}
            />
          );
        },
      },
      {
        accessorKey: "modeDuration",
        header: "Duration (days)",
        size: 85,
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ row }: { row: MRT_Row<RiskBaseModel> }) => {
          return (
            <RNumberInputLabel
              value={row.original.modeDuration ?? 0}
              category={"duration"}
              onInputChange={(newValue) => {
                void handleSaveCell(row.original, "modeDuration", newValue);
              }}
              decimalScaleInput={6}
              decimalScaleLabel={2}
              allowNegative
            />
          );
        },
      },
      {
        accessorKey: "fixedDeterministicPerformanceCost",
        header: "Duration Cost",
        minSize: 75,
        size: 90,
        maxSize: 150,
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ row }: { row: MRT_Row<RiskBaseModel> }) => {
          return (
            <RNumberLabel value={row.original.durationCost ?? 0} category={"currency"} />
          );
        },
      },
      {
        accessorKey: "modeRemedialCost",
        header: "Remedial Cost",
        minSize: 75,
        size: 90,
        maxSize: 150,
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ row }: { row: MRT_Row<RiskBaseModel> }) => {
          return (
            <RNumberInputLabel
              value={row.original.modeRemedialCost ?? 0}
              category={"currency"}
              onInputChange={(newValue) => {
                void handleSaveCell(row.original, "modeRemedialCost", newValue);
              }}
              decimalScaleInput={6}
              decimalScaleLabel={2}
              range={SMALL_AMOUNT_RANGE}
              allowNegative
            />
          );
        },
      },
      {
        accessorKey: "modeEmissions",
        header: "Emissions",
        minSize: 75,
        size: 90,
        maxSize: 150,
        muiTableBodyCellProps: {
          align: "right",
        },
        Cell: ({ row }: { row: MRT_Row<RiskBaseModel> }) => {
          return (
            <RNumberInputLabel
              value={row.original.modeEmissions ?? 0}
              category={"emissions"}
              onInputChange={(newValue) => {
                void handleSaveCell(row.original, "modeEmissions", newValue);
              }}
              decimalScaleInput={6}
              decimalScaleLabel={2}
              range={SMALL_AMOUNT_RANGE}
              allowNegative
            />
          );
        },
      },
      {
        accessorKey: "impactCost",
        header: "Impact: Cost",
        size: 100,
        enableEditing: false,
        muiTableBodyCellProps: {
          align: "right",
        },
        muiTableHeadCellProps: () => ({
          style: { backgroundColor: "var(--ecerto-red)" },
          sx: {
            "& .MuiDivider-root": {
              border: "2px solid var(--ecerto-red-dark) !important",
            },
          },
        }),

        Cell: ({ row }: { row: MRT_Row<RiskBaseModel> }) => {
          return (
            <Box
              sx={{
                marginRight: "0.5rem",
              }}>
              <RNumberLabel value={row.original.impactCost ?? 0} category={"currency"} />
            </Box>
          );
        },
        Footer: () => (
          <Footer id="impactCost" items={overviewItems} category={"currency"} />
        ),
      },
      {
        accessorKey: "impactDuration",
        header: "Impact: Duration (days)",
        size: 110,
        enableEditing: false,
        muiTableBodyCellProps: {
          align: "right",
        },
        muiTableHeadCellProps: () => ({
          style: { backgroundColor: "var(--ecerto-red)" },
          sx: {
            "& .MuiDivider-root": {
              border: "2px solid var(--ecerto-red-dark) !important",
            },
          },
        }),
        Cell: ({ row }: { row: MRT_Row<RiskBaseModel> }) => {
          return (
            <Box
              sx={{
                marginRight: "0.5rem",
              }}>
              <RNumberLabel
                value={row.original.impactDuration ?? 0}
                category={"duration"}
              />
            </Box>
          );
        },
        Footer: () => (
          <Footer id="impactDuration" items={overviewItems} category={"duration"} />
        ),
      },
      {
        accessorKey: "impactEmissions",
        header: "Impact: Emissions",
        size: 100,
        enableEditing: false,
        muiTableBodyCellProps: {
          align: "right",
        },
        muiTableHeadCellProps: () => ({
          style: { backgroundColor: "var(--ecerto-red)" },
          sx: {
            "& .MuiDivider-root": {
              border: "2px solid var(--ecerto-red-dark) !important",
            },
          },
        }),
        Cell: ({ row }: { row: MRT_Row<RiskBaseModel> }) => {
          return (
            <Box
              sx={{
                marginRight: "0.5rem",
              }}>
              <RNumberLabel
                value={row.original.impactEmissions ?? 0}
                category={"emissions"}
              />
            </Box>
          );
        },
        Footer: () => (
          <Footer id="impactEmissions" items={overviewItems} category={"emissions"} />
        ),
      },
    ],
    [overviewItems, handleSaveCell]
  );

export default ColumnsForRiskTable;
